import { LogOut, User, Coins, Plus, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useGetCreditsBalance } from "@/hooks/api/query/useGetCreditsBalance";
import { supabase } from "@/integrations/supabase/client";
import { PaywallDialog } from "@/components/PaywallDialog/PaywallDialog";
import { queryKeys } from "../../lib/queryClient";

export function UserAvatarDropdown() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<{
    first_name: string;
    last_name: string;
  } | null>(null);
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);

  // Fetch credits using React Query
  const { data: creditsData, isLoading: isLoadingCredits } =
    useGetCreditsBalance({
      enabled: !!user,
      queryKey: queryKeys.credits.balance(),
    });

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("user_id", user.id)
        .single();

      if (data) {
        setProfile(data);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  const handleProfileClick = () => {
    navigate("/profil");
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  if (!user) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="group relative h-8 w-8 rounded-full hover:bg-transparent hover:text-inherit focus-visible:ring-white/40"
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground font-semibold transition-colors group-hover:bg-primary/80">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-72 z-50 bg-popover border-border p-2"
          align="end"
          sideOffset={4}
          avoidCollisions={true}
          forceMount
        >
          <DropdownMenuLabel className="font-normal px-2 pt-1 pb-2">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {profile?.first_name && profile?.last_name
                  ? `${profile.first_name} ${profile.last_name}`
                  : "Utilizator"}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            </div>
          </DropdownMenuLabel>

          {/* Credits Card */}
          <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-primary/15 p-3 my-1">
            <div className="absolute -top-6 -right-6 w-20 h-20 bg-primary/10 rounded-full blur-2xl" />
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <div className="p-1 rounded-md bg-primary/10">
                    <Coins className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Credite rămase
                  </span>
                </div>
                <Sparkles className="h-3.5 w-3.5 text-primary/60" />
              </div>
              <div className="flex items-baseline gap-1.5 mb-3">
                {isLoadingCredits ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <>
                    <span className="text-2xl font-extrabold text-primary font-mono leading-none">
                      {creditsData?.credits?.toLocaleString() ?? 0}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      credite
                    </span>
                  </>
                )}
              </div>
              <Button
                size="sm"
                className="w-full h-8 text-xs font-semibold"
                onClick={() => setBuyDialogOpen(true)}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Cumpără credite
              </Button>
            </div>
          </div>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleProfileClick}>
            <User className="mr-2 h-4 w-4" />
            <span>Profil</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Deconectează-te</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PaywallDialog
        open={buyDialogOpen}
        onOpenChange={setBuyDialogOpen}
        mode="buy"
      />
    </>
  );
}
